import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Envelope, Lock, GoogleLogo, ArrowRight, Eye, EyeSlash } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Reusable logo component: Mater (gray) + Match (orange)
// variant='light' for dark backgrounds (white Mater), 'default' for light backgrounds (gray Mater)
const BrandLogo = ({ size = 'md', variant = 'default', className = '' }) => {
  const imgClass = size === 'lg' ? 'h-20 w-auto' : size === 'sm' ? 'h-8 w-auto' : 'h-12 w-auto';
  const textClass = size === 'lg' ? 'text-3xl' : size === 'sm' ? 'text-lg' : 'text-2xl';
  const materColor = variant === 'light' ? 'text-white' : 'text-zinc-600 dark:text-zinc-300';
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <img src="/matermatch-logo.png" alt="MaterMatch" className={imgClass} />
      <div>
        <span className={`${textClass} font-black ${materColor} tracking-tight`}>Mater</span>
        <span className={`${textClass} font-black text-orange-500 tracking-tight`}>Match</span>
      </div>
    </div>
  );
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleGoogleLogin = () => {
  const redirectUrl = window.location.origin + '/dashboard';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const fromLoc = location.state?.from;
  const from = fromLoc?.pathname ? fromLoc.pathname + (fromLoc.search || '') : '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('¡Bienvenido de vuelta!');
      navigate(from, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al iniciar sesión';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 dark:bg-zinc-950 flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1644411813513-ad77c1b77581?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3NTA2NTAyMHww&ixlib=rb-4.1.0&q=85"
          alt="Construction site"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/80 to-zinc-950/40 flex items-center justify-center">
          <div className="text-white p-12">
            <BrandLogo size="lg" variant="light" className="mb-6" />
            <p className="text-lg text-zinc-300 max-w-md mt-4">
              Cotiza. Compara. Construye.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white dark:bg-zinc-900">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <BrandLogo size="md" />
          </div>

          <h2 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">Iniciar Sesión</h2>
          <p className="text-zinc-600 dark:text-zinc-400 mb-8">Ingresa a tu cuenta para continuar</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Correo electrónico</Label>
              <div className="relative">
                <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@correo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 rounded-none"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 rounded-none"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                >
                  {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Link 
                to="/forgot-password" 
                className="text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200"
                data-testid="forgot-password-link"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary rounded-none h-12"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Iniciando...' : 'Iniciar Sesión'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-300 dark:border-zinc-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-zinc-900 text-zinc-500">o continúa con</span>
            </div>
          </div>

          <Button 
            type="button"
            variant="outline"
            className="w-full rounded-none h-12 border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <GoogleLogo className="w-5 h-5 mr-2" weight="bold" />
            Google
          </Button>

          <p className="text-center mt-8 text-zinc-600 dark:text-zinc-400">
            ¿No tienes cuenta?{' '}
            <Link 
              to="/register" 
              className="text-zinc-900 dark:text-zinc-200 font-semibold hover:underline"
              data-testid="register-link"
            >
              Regístrate aquí
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedDataDeletion, setAcceptedDataDeletion] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const allLegalAccepted = acceptedTerms && acceptedPrivacy && acceptedDataDeletion;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (!allLegalAccepted) {
      toast.error('Debes aceptar los tres documentos legales para crear tu cuenta.');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(email, password, name, {
        accepted_terms: acceptedTerms,
        accepted_privacy: acceptedPrivacy,
        accepted_data_deletion: acceptedDataDeletion,
      });
      toast.success('¡Registro exitoso! Bienvenido a MaterMatch');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al registrarse';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 dark:bg-zinc-950 flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.pexels.com/photos/392031/pexels-photo-392031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Construction crane"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/80 to-zinc-950/40 flex items-center justify-center">
          <div className="text-white p-12">
            <BrandLogo size="lg" variant="light" className="mb-6" />
            <p className="text-lg text-zinc-300 max-w-md mt-4">
              Cotiza. Compara. Construye.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white dark:bg-zinc-900">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <BrandLogo size="md" />
          </div>

          <h2 className="text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">Crear Cuenta</h2>
          <p className="text-zinc-600 dark:text-zinc-400 mb-8">Regístrate para comenzar</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre completo</Label>
              <Input
                id="name"
                type="text"
                placeholder="Tu nombre"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-none"
                required
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@correo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none"
                required
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Mínimo 6 caracteres"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pr-10 rounded-none"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                >
                  {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Repite tu contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="rounded-none"
                required
                data-testid="register-confirm-password-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full btn-primary rounded-none h-12"
              disabled={loading || !allLegalAccepted}
              data-testid="register-submit-btn"
            >
              {loading ? 'Registrando...' : 'Crear Cuenta'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            {/* Mandatory legal acceptance block */}
            <div className="border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 p-4 space-y-3" data-testid="register-legal-block">
              <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
                Aceptación obligatoria
              </p>

              <label className="flex items-start gap-3 cursor-pointer group" data-testid="accept-terms-label">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  required
                  className="mt-1 w-4 h-4 accent-orange-500 flex-shrink-0"
                  data-testid="accept-terms-checkbox"
                />
                <span className="text-sm text-zinc-700 dark:text-zinc-300 leading-snug">
                  He leído y acepto los{' '}
                  <a
                    href="/api/legal/terms-conditions/page"
                    target="_blank"
                    rel="noreferrer"
                    className="text-orange-600 dark:text-orange-400 underline font-medium hover:text-orange-700"
                    data-testid="terms-link"
                  >
                    Términos y Condiciones
                  </a>
                  .
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group" data-testid="accept-privacy-label">
                <input
                  type="checkbox"
                  checked={acceptedPrivacy}
                  onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                  required
                  className="mt-1 w-4 h-4 accent-orange-500 flex-shrink-0"
                  data-testid="accept-privacy-checkbox"
                />
                <span className="text-sm text-zinc-700 dark:text-zinc-300 leading-snug">
                  He leído y acepto el{' '}
                  <a
                    href="/api/legal/privacy-policy/page"
                    target="_blank"
                    rel="noreferrer"
                    className="text-orange-600 dark:text-orange-400 underline font-medium hover:text-orange-700"
                    data-testid="privacy-link"
                  >
                    Aviso de Privacidad
                  </a>
                  .
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group" data-testid="accept-data-deletion-label">
                <input
                  type="checkbox"
                  checked={acceptedDataDeletion}
                  onChange={(e) => setAcceptedDataDeletion(e.target.checked)}
                  required
                  className="mt-1 w-4 h-4 accent-orange-500 flex-shrink-0"
                  data-testid="accept-data-deletion-checkbox"
                />
                <span className="text-sm text-zinc-700 dark:text-zinc-300 leading-snug">
                  He leído y acepto la{' '}
                  <a
                    href="/api/legal/data-deletion/page"
                    target="_blank"
                    rel="noreferrer"
                    className="text-orange-600 dark:text-orange-400 underline font-medium hover:text-orange-700"
                    data-testid="data-deletion-link"
                  >
                    Política de Eliminación de Cuenta
                  </a>
                  .
                </span>
              </label>

              {!allLegalAccepted && (
                <p className="text-xs text-zinc-500 dark:text-zinc-400 pt-1 italic">
                  Debes aceptar los tres documentos para crear tu cuenta.
                </p>
              )}
            </div>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-300 dark:border-zinc-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-zinc-900 text-zinc-500">o regístrate con</span>
            </div>
          </div>

          <Button 
            type="button"
            variant="outline"
            className="w-full rounded-none h-12 border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleGoogleLogin}
            disabled={!allLegalAccepted}
            title={allLegalAccepted ? '' : 'Acepta los documentos legales antes de continuar'}
            data-testid="google-register-btn"
          >
            <GoogleLogo className="w-5 h-5 mr-2" weight="bold" />
            Google
          </Button>

          <p className="text-center mt-8 text-zinc-600 dark:text-zinc-400">
            ¿Ya tienes cuenta?{' '}
            <Link 
              to="/login" 
              className="text-zinc-900 dark:text-zinc-200 font-semibold hover:underline"
              data-testid="login-link"
            >
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
